import { useLayoutEffect, useRef } from "react";
import { FIT_LINE_HEIGHT, FIT_TEXT_CLASS, useFitToBox } from "./useFitToBox";

/**
 * Editable option text, rendered exactly like the read-only version.
 *
 * A `contentEditable` element rather than a textarea, for one reason: a
 * textarea cannot centre its content vertically, and it brings its own metrics
 * and placeholder rendering. Sharing the same element type, the same classes
 * and the same fit hook as `FitText` is what makes writing an option look
 * identical to reading it — the whole point being that the player is editing
 * the thing they will later see.
 *
 * Deliberately uncontrolled: writing `value` back into the DOM on every
 * keystroke moves the caret to the start. The DOM is only written to when the
 * value changes from outside — random or clear.
 */
export const FitInput = ({
  value,
  onChange,
  maxLength,
  disabled,
  placeholder,
  color,
  max = 40,
  min = 13,
}: {
  value: string;
  onChange: (v: string) => void;
  maxLength: number;
  disabled?: boolean;
  placeholder?: string;
  color: string;
  max?: number;
  min?: number;
}) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const editRef = useRef<HTMLDivElement>(null);

  /*
   * Registered *before* `useFitToBox`, and as a layout effect rather than a
   * passive one. Both matter, and for the same reason: layout effects run in
   * the order they were registered, before any passive effect.
   *
   * Typing updates the DOM itself, so the fitter always saw current text for
   * that path. A value arriving from outside — a server suggestion, or clear —
   * did not: the fitter ran first against the *previous* content, sized for it,
   * and then this effect wrote the new text in. Nothing re-measured, because
   * the ResizeObserver watches the box and only the text had changed. A long
   * suggestion therefore rendered at the size chosen for an empty field and
   * pushed the card open.
   */
  useLayoutEffect(() => {
    const el = editRef.current;
    if (el && el.textContent !== value) el.textContent = value;
  }, [value]);

  const size = useFitToBox(boxRef, editRef, value, { min, max });

  const handleInput = () => {
    const el = editRef.current;
    if (!el) return;

    const text = el.textContent ?? "";
    if (text.length > maxLength) {
      // Truncate and put the caret back at the end, or it jumps to the start.
      el.textContent = text.slice(0, maxLength);
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
      onChange(el.textContent ?? "");
      return;
    }

    onChange(text);
  };

  return (
    /*
     * `absolute inset-0` inside a relative parent, so the measured box is
     * exactly the space available and cannot be enlarged by the very text being
     * measured. As a normal-flow `h-full` element it grew with its content
     * whenever the ancestor height was not definite — `scrollHeight <=
     * clientHeight` was then always true, the font never shrank, and the text
     * pushed the card open until the second one had nowhere left to go.
     */
    <div className="relative h-full w-full">
      <div
        ref={boxRef}
        className="absolute inset-0 flex items-center justify-center overflow-hidden"
        onClick={() => editRef.current?.focus()}
      >
        <div className="relative w-full">
        {value.length === 0 && placeholder && (
          <span
            aria-hidden="true"
            className={`pointer-events-none absolute inset-0 ${FIT_TEXT_CLASS}`}
            style={{ fontSize: size, lineHeight: FIT_LINE_HEIGHT, color, opacity: 0.5 }}
          >
            {placeholder}
          </span>
        )}
        <div
          ref={editRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label={placeholder}
          onInput={handleInput}
          // Single-line paste and no rich text: the value is plain text.
          onPaste={(e) => {
            e.preventDefault();
            const text = e.clipboardData.getData("text/plain").replace(/\s+/g, " ");
            document.execCommand("insertText", false, text);
          }}
          style={{ fontSize: size, lineHeight: FIT_LINE_HEIGHT, color, caretColor: color }}
          className={`${FIT_TEXT_CLASS} outline-none`}
          />
        </div>
      </div>
    </div>
  );
};
